import {
  LoaderCircle,
  Play,
  Square,
  Volume1,
  Volume2,
  VolumeX,
} from "lucide-react";

import { airedFraction } from "../api/polling.js";
import { cn } from "../design-system/cn.js";
import { formatDuration } from "../lib/format.js";
import { usePlayer, type PlaybackState } from "./player-provider.js";
import type { NowPlaying } from "../api/types.js";

const SIZES = {
  small: "size-9 [&_svg]:size-3.5",
  medium: "size-11 [&_svg]:size-4",
  large: "size-16 [&_svg]:size-6",
} as const;

/**
 * Play and stop, and nothing else. The stream is live: there is no seek, no
 * previous, no next, and pausing is stopping.
 */
export function PlayButton({
  className,
  disabled = false,
  feedName,
  onActivate,
  size = "medium",
  state,
  tone = "solid",
}: {
  className?: string;
  disabled?: boolean;
  feedName: string;
  onActivate: () => void;
  size?: keyof typeof SIZES;
  state: PlaybackState;
  /**
   * Solid where listening is the point of the screen; quiet where it is one verb
   * among several and the accent would be spent on the least consequential of them.
   */
  tone?: "solid" | "quiet";
}) {
  const busy = state === "connecting" || state === "stalled";
  const stopping = state !== "idle" && state !== "error" && state !== "blocked";
  const label = stopping ? `Stop ${feedName}` : `Play ${feedName}`;

  return (
    <button
      aria-label={label}
      className={cn(
        "inline-grid shrink-0 place-items-center rounded-full border border-transparent transition-[background-color,transform] active:scale-95 disabled:pointer-events-none disabled:opacity-45",
        tone === "solid"
          ? "bg-accent text-accent-ink hover:bg-accent-dim"
          : stopping
            ? "bg-transparent text-accent-text hover:bg-surface-hi"
            : "bg-transparent text-ink-2 hover:bg-surface-hi hover:text-ink",
        SIZES[size],
        className,
      )}
      disabled={disabled}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onActivate();
      }}
      title={label}
      type="button"
    >
      {busy ? (
        <LoaderCircle
          aria-hidden="true"
          className="animate-[radio-spin_1s_linear_infinite]"
        />
      ) : stopping ? (
        <Square aria-hidden="true" fill="currentColor" />
      ) : (
        <Play
          aria-hidden="true"
          className="translate-x-px"
          fill="currentColor"
        />
      )}
    </button>
  );
}

export function VolumeControl({ className }: { className?: string }) {
  const { muted, setVolume, toggleMuted, volume } = usePlayer();
  const Icon =
    muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <button
        aria-label={muted ? "Unmute" : "Mute"}
        className="grid size-9 shrink-0 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-surface-hi hover:text-ink"
        onClick={toggleMuted}
        type="button"
      >
        <Icon aria-hidden="true" className="size-4" />
      </button>
      <input
        aria-label="Volume"
        className="h-1 w-24 cursor-pointer appearance-none rounded-full bg-border-hi [accent-color:var(--accent)]"
        max={1}
        min={0}
        onChange={(event) => {
          setVolume(Number(event.target.value));
        }}
        step={0.01}
        style={{ accentColor: "var(--accent)" }}
        type="range"
        value={muted ? 0 : volume}
      />
    </div>
  );
}

/**
 * How far into its current track a feed is. It is a readout, not a control —
 * there is nothing behind a live stream to seek to, so it takes no pointer events
 * and carries no handle.
 */
export function AiredProgress({
  className,
  now,
  snapshot,
  withTimes = false,
}: {
  className?: string;
  now: number;
  snapshot: NowPlaying | null;
  withTimes?: boolean;
}) {
  const airing = snapshot?.nowPlaying ?? null;
  const fraction = airedFraction(snapshot, now);
  const elapsed = airing
    ? Math.min(
        airing.durationMs,
        Math.max(0, now - Date.parse(airing.startedAt)),
      )
    : 0;

  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      {withTimes ? (
        <span className="w-9 shrink-0 text-right font-mono text-[0.6875rem] text-ink-3 tabular-nums">
          {airing ? formatDuration(elapsed) : "--:--"}
        </span>
      ) : null}
      <div
        aria-hidden="true"
        className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-border"
      >
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-1000 ease-linear"
          style={{ width: `${String(Math.round(fraction * 100))}%` }}
        />
      </div>
      {withTimes ? (
        <span className="w-9 shrink-0 font-mono text-[0.6875rem] text-ink-3 tabular-nums">
          {airing ? formatDuration(airing.durationMs) : "--:--"}
        </span>
      ) : null}
    </div>
  );
}
